import type React from "react";
import { useEffect, useState } from "react";
import ApiService from "../services/api";

interface VerificationCodeSectionProps {
	email: string;
	isVisible: boolean;
	onVerificationSuccess: (userId: number) => void;
	onRequestNewCode: () => void;
	cooldownSeconds?: number;
	errorMessage?: string;
}

export const VerificationCodeSection: React.FC<
	VerificationCodeSectionProps
> = ({
	email,
	isVisible,
	onVerificationSuccess,
	onRequestNewCode,
	cooldownSeconds: initialCooldown,
	errorMessage,
}) => {
	const [code, setCode] = useState("");
	const [isVerifying, setIsVerifying] = useState(false);
	const [verificationError, setVerificationError] = useState<string | null>(
		null,
	);
	const [cooldownSeconds, setCooldownSeconds] = useState(initialCooldown || 0);

	// Countdown timer for rate limiting
	useEffect(() => {
		if (cooldownSeconds > 0) {
			const timer = setInterval(() => {
				setCooldownSeconds((prev) => Math.max(0, prev - 1));
			}, 1000);
			return () => clearInterval(timer);
		}
	}, [cooldownSeconds]);

	// Update cooldown when prop changes
	useEffect(() => {
		if (initialCooldown !== undefined) {
			setCooldownSeconds(initialCooldown);
		}
	}, [initialCooldown]);

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/\D/g, "").slice(0, 6);
		setCode(value);
		if (verificationError) setVerificationError(null);
	};

	const handleVerifyCode = async () => {
		if (code.length !== 6) {
			setVerificationError("Please enter a 6-digit code");
			return;
		}

		setIsVerifying(true);
		setVerificationError(null);

		try {
			const data = await ApiService.verifyCode(email, code);

			if (data.success && data.user_id) {
				onVerificationSuccess(data.user_id);
			} else {
				setVerificationError(data.message || "Verification failed");
			}
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : "Unknown error";

			// Provide user-friendly error messages
			if (errorMessage.includes("405")) {
				setVerificationError(
					"Service temporarily unavailable. Please try again in a moment.",
				);
			} else if (
				errorMessage.includes("network") ||
				errorMessage.includes("fetch")
			) {
				setVerificationError(
					"Network error. Please check your connection and try again.",
				);
			} else {
				setVerificationError(
					`Verification failed: ${errorMessage}. Please try again or request a new code.`,
				);
			}
		} finally {
			setIsVerifying(false);
		}
	};

	const formatCooldown = (seconds: number): string => {
		if (seconds >= 3600) {
			const hours = Math.floor(seconds / 3600);
			const mins = Math.floor((seconds % 3600) / 60);
			return `${hours}h ${mins}m`;
		}
		if (seconds >= 60) {
			const mins = Math.floor(seconds / 60);
			const secs = seconds % 60;
			return `${mins}m ${secs}s`;
		}
		return `${seconds}s`;
	};

	if (!isVisible) return null;

	return (
		<section id="verification-code-section">
			<h2>Verify Your Email</h2>
			<p>
				We've sent a 6-digit verification code to <strong>{email}</strong>
			</p>
			<p style={{ fontSize: "14px", color: "#666" }}>
				The code will expire in 1 minute.
			</p>

			<div className="verification-form" style={{ margin: "20px 0" }}>
				<label htmlFor="verification-code">
					Verification Code:
					<input
						id="verification-code"
						type="text"
						value={code}
						onChange={handleCodeChange}
						placeholder="000000"
						maxLength={6}
						style={{
							fontSize: "24px",
							letterSpacing: "8px",
							textAlign: "center",
							width: "200px",
							padding: "12px",
							margin: "10px 0",
						}}
						disabled={isVerifying}
					/>
				</label>

				<button
					type="button"
					onClick={handleVerifyCode}
					disabled={code.length !== 6 || isVerifying}
					style={{
						padding: "12px 24px",
						fontSize: "16px",
						fontWeight: "bold",
						marginTop: "10px",
						cursor:
							code.length !== 6 || isVerifying ? "not-allowed" : "pointer",
						opacity: code.length !== 6 || isVerifying ? 0.6 : 1,
					}}
				>
					{isVerifying ? "Verifying..." : "Verify Code"}
				</button>
			</div>

			{verificationError && (
				<div
					className="error-message"
					style={{
						color: "#d32f2f",
						padding: "10px",
						marginTop: "10px",
						backgroundColor: "#ffebee",
						borderRadius: "4px",
					}}
				>
					{verificationError}
				</div>
			)}

			{errorMessage && (
				<div
					className="error-message"
					style={{
						color: "#d32f2f",
						padding: "10px",
						marginTop: "10px",
						backgroundColor: "#ffebee",
						borderRadius: "4px",
					}}
				>
					{errorMessage}
				</div>
			)}

			<div style={{ marginTop: "20px" }}>
				{cooldownSeconds > 0 ? (
					<p style={{ color: "#666" }}>
						You can request a new code in{" "}
						<strong>{formatCooldown(cooldownSeconds)}</strong>
					</p>
				) : (
					<button
						type="button"
						onClick={onRequestNewCode}
						style={{
							padding: "8px 16px",
							fontSize: "14px",
							background: "transparent",
							border: "1px solid #666",
							color: "#333",
							cursor: "pointer",
						}}
					>
						Resend Code
					</button>
				)}
			</div>

			<p style={{ fontSize: "12px", color: "#999", marginTop: "20px" }}>
				Didn't receive the code? Check your spam folder or request a new code.
			</p>
		</section>
	);
};
